import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';

/**
 * Cancel Auto-Renewal modal — captures a structured reason + optional follow-up consent.
 * Round 2++ retention research. HNW tone: single-screen flow, no nags, no upsells.
 */
const REASONS = [
  { value: 'missing_feature',          label: 'Missing a feature I need',  detailPlaceholder: 'Optional — which feature?', detailRequired: false },
  { value: 'hard_to_navigate',         label: 'Hard to navigate or use',   detailPlaceholder: null,                        detailRequired: false },
  { value: 'not_enough_value',         label: 'Not getting enough value',  detailPlaceholder: null,                        detailRequired: false },
  { value: 'privacy_security_concerns',label: 'Privacy or security concerns', detailPlaceholder: 'Optional — what concerned you?', detailRequired: false },
  { value: 'found_alternative',        label: 'Found an alternative',       detailPlaceholder: 'Which one? (optional)',     detailRequired: false },
  { value: 'other',                    label: 'Other',                     detailPlaceholder: 'Please describe (required)', detailRequired: true  },
];

const CancelReasonModal = ({ isOpen, onClose, onConfirm, subscriberUntil, busy }) => {
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [mayFollowUp, setMayFollowUp] = useState(false);
  const [touched, setTouched] = useState(false);

  const selected = REASONS.find(r => r.value === reason);
  const needsDetail = selected?.detailRequired === true;
  const detailMissing = needsDetail && detail.trim().length === 0;
  const canSubmit = !!reason && !detailMissing && !busy;

  const handleClose = () => {
    if (busy) return;
    setReason(''); setDetail(''); setMayFollowUp(false); setTouched(false);
    onClose();
  };

  const handleSubmit = () => {
    setTouched(true);
    if (!canSubmit) return;
    onConfirm({
      reason,
      reason_detail: detail.trim().slice(0, 500) || null,
      may_follow_up: mayFollowUp,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg" data-testid="cancel-reason-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-serif text-slate-900">Cancel auto-renewal?</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Your access continues until {subscriberUntil ? new Date(subscriberUntil).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : 'your renewal date'} — nothing changes today.
            We'd appreciate one word on why you're leaving so we can improve.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <RadioGroup value={reason} onValueChange={(v) => { setReason(v); setDetail(''); setTouched(false); }} data-testid="cancel-reason-radio-group">
            {REASONS.map((r) => (
              <div key={r.value} className="space-y-2">
                <div className="flex items-start gap-2">
                  <RadioGroupItem value={r.value} id={`reason-${r.value}`} data-testid={`cancel-reason-${r.value}`} />
                  <Label htmlFor={`reason-${r.value}`} className="text-sm text-slate-800 leading-relaxed cursor-pointer">
                    {r.label}
                  </Label>
                </div>
                {reason === r.value && r.detailPlaceholder && (
                  <div className="ml-6">
                    <Textarea
                      value={detail}
                      onChange={(e) => setDetail(e.target.value.slice(0, 500))}
                      placeholder={r.detailPlaceholder}
                      rows={2}
                      className={`text-sm ${touched && detailMissing ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
                      maxLength={500}
                      data-testid="cancel-reason-detail-input"
                    />
                    <div className="flex justify-between mt-1">
                      <span className={`text-xs ${touched && detailMissing ? 'text-red-600' : 'text-slate-400'}`}>
                        {touched && detailMissing ? 'Please describe your reason' : `${detail.length}/500`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </RadioGroup>

          <div className="flex items-start gap-2 pt-2 border-t border-slate-100">
            <Checkbox
              id="cancel-follow-up"
              checked={mayFollowUp}
              onCheckedChange={(c) => setMayFollowUp(c === true)}
              data-testid="cancel-may-follow-up-checkbox"
            />
            <Label htmlFor="cancel-follow-up" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
              It's OK to email me once to ask a follow-up question about my reason. We respect your time — one message only.
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleClose} disabled={busy} data-testid="cancel-reason-keep-btn">
            Keep auto-renewal on
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="cancel-reason-submit-btn"
          >
            {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Cancelling…</> : 'Cancel auto-renewal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelReasonModal;
